import {
  Check as CheckIcon,
  Computer as ComputerIcon,
  ContentCopy as CopyIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  Pending as PendingIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNotification } from "../../contexts/NotificationContext";
import { userProductHardwareService } from "../../services/userProductHardwareService";
import { userProductLicenseService } from "../../services/userProductLicenseService";
import { isLicenseServerAssignment } from "../../utils/licenseServerUtils";
import {
  calculateLicenseStatus,
  getDaysUntilExpiry,
} from "../../utils/licenseStatusUtils";

const HardwareIdSubmission = ({ products = [] }) => {
  const [hardwareSubmissions, setHardwareSubmissions] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [hardwareId, setHardwareId] = useState("");
  const { user } = useAuth();
  const { showNotification } = useNotification();

  useEffect(() => {
    loadHardwareSubmissions();
  }, []);

  const loadHardwareSubmissions = async () => {
    try {
      setLoading(true);

      // Load hardware submissions
      const response = await userProductHardwareService.getMyHardwareIds();
      setHardwareSubmissions(response.hardwareSubmissions || []);

      // Load licenses
      try {
        const licensesData = await userProductLicenseService.getMyLicenses();
        setLicenses(licensesData.productLicenses || []);
      } catch (licenseErr) {
        console.error("Error loading licenses:", licenseErr);
        // Don't set error for license loading failure
      }

      setError(null);
    } catch (err) {
      console.error("Error loading hardware submissions:", err);
      setError("Failed to load hardware ID submissions");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitHardwareId = async () => {
    if (!selectedProduct || !hardwareId.trim()) {
      setError("Please enter a valid hardware ID");
      return;
    }

    try {
      setSubmitting(true);
      await userProductHardwareService.submitHardwareId(selectedProduct.id, {
        hardwareId: hardwareId.trim(),
      });

      setSuccess(
        "Hardware ID submitted successfully! Waiting for admin confirmation.",
      );
      setDialogOpen(false);
      setHardwareId("");
      setSelectedProduct(null);

      // Reload submissions
      await loadHardwareSubmissions();
    } catch (err) {
      console.error("Error submitting hardware ID:", err);
      setError(err.response?.data?.message || "Failed to submit hardware ID");
    } finally {
      setSubmitting(false);
    }
  };

  const openSubmissionDialog = (product) => {
    setSelectedProduct(product);

    // Check if there's an existing submission for this product
    const existingSubmission = hardwareSubmissions.find(
      (sub) => sub.product.id === product.id,
    );

    if (existingSubmission && !existingSubmission.is_locked) {
      setHardwareId(existingSubmission.hardware_id);
    } else {
      setHardwareId("");
    }

    setDialogOpen(true);
    setError(null);
    setSuccess(null);
  };

  const getSubmissionStatus = (product) => {
    const submission = hardwareSubmissions.find(
      (sub) => sub.product.id === product.id,
    );

    if (!submission) {
      return { status: "none", label: "Not Submitted", color: "error" };
    }

    if (submission.is_confirmed && submission.is_locked) {
      return { status: "confirmed", label: "Active", color: "success" };
    }

    if (submission.is_locked) {
      return { status: "locked", label: "Locked", color: "warning" };
    }

    return { status: "pending", label: "Pending Approval", color: "warning" };
  };

  const canSubmitOrEdit = (product) => {
    const submission = hardwareSubmissions.find(
      (sub) => sub.product.id === product.id,
    );

    // Can submit if no submission exists, or if submission exists but is not locked
    return !submission || !submission.is_locked;
  };

  const getLicenseForProduct = (productId) => {
    // Find the license data from licenses using the updated structure
    const license = licenses.find(
      (license) => license.product_id === productId,
    );

    if (!license) {
      return null;
    }

    // Return license regardless of active status - let the UI handle status display
    if (license && license.license_key) {
      return license;
    }
    return null;
  };

  const copyLicenseKey = async (license, productName) => {
    try {
      await navigator.clipboard.writeText(license.license_key);
      if (isLicenseExpired(license)) {
        showNotification(
          `License key copied for ${productName}! Note: This license has expired.`,
          "warning",
        );
      } else {
        showNotification(`License key copied for ${productName}!`);
      }
    } catch (err) {
      console.error("Failed to copy license key:", err);
      showNotification("Failed to copy license key to clipboard", "error");
    }
  };

  // Use centralized license status utilities
  const isLicenseExpiring = (license) => {
    if (!license || !license.license_key) return false;
    // Check if license is inactive
    if (license.is_active === false) return false;

    const status = calculateLicenseStatus(license);
    return status === "expiring_soon";
  };

  const isLicenseExpired = (license) => {
    if (!license || !license.license_key) return false;
    const status = calculateLicenseStatus(license);
    return status === "expired";
  };

  const isLicenseInactive = (license) => {
    if (!license) return false;
    return license.is_active === false;
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Filter products that require licenses. License-server assignments are
  // excluded: those users host licenses for the product rather than consuming
  // one, so they never submit a hardware ID (the API rejects it too — see
  // userProductHardware.controller). Filtering here rather than at each call
  // site keeps every consumer of this component consistent.
  const licensedProducts = products.filter(
    (product) =>
      product.requires_license && !isLicenseServerAssignment(product),
  );

  if (licensedProducts.length === 0) {
    return (
      <Alert severity="info">
        You don't have any products assigned that require hardware ID
        submission.
      </Alert>
    );
  }

  // Check if any products need hardware ID submission
  const productsNeedingSubmission = licensedProducts.filter((product) => {
    const status = getSubmissionStatus(product);
    return status.status === "none";
  });

  const productsPendingConfirmation = licensedProducts.filter((product) => {
    const status = getSubmissionStatus(product);
    return status.status === "pending";
  });

  return (
    <Box>
      <Card elevation={2} sx={{ mb: 2 }}>
        <CardContent>
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}
          {success && (
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              onClose={() => setSuccess(null)}
            >
              {success}
            </Alert>
          )}

          {/* Action Required Alerts */}
          {productsNeedingSubmission.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2, border: "none" }}>
              <Typography variant="subtitle2" gutterBottom>
                Action Required: Hardware ID Submission
              </Typography>
              <Typography variant="body2">
                You have {productsNeedingSubmission.length} product(s) that
                require hardware ID submission before licenses can be generated.
                Please submit your hardware IDs below.
              </Typography>
            </Alert>
          )}

          {productsPendingConfirmation.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Waiting for Admin Confirmation
              </Typography>
              <Typography variant="body2">
                You have {productsPendingConfirmation.length} hardware ID(s)
                pending admin confirmation. You'll be notified once they are
                approved.
              </Typography>
            </Alert>
          )}

          <List>
            {licensedProducts.map((product, index) => {
              const status = getSubmissionStatus(product);
              const canEdit = canSubmitOrEdit(product);
              const submission = hardwareSubmissions.find(
                (sub) => sub.product.id === product.id,
              );
              const license = getLicenseForProduct(product.id);

              return (
                <React.Fragment key={product.id}>
                  <ListItem
                    sx={{
                      alignItems: "flex-start",
                      py: 3,
                      px: 3,
                      minHeight: "auto",
                      border: "1px solid",
                      borderColor: license
                        ? isLicenseInactive(license)
                          ? "grey.400"
                          : isLicenseExpired(license)
                            ? "error.main"
                            : isLicenseExpiring(license)
                              ? "warning.main"
                              : "success.main"
                        : "divider",
                      borderRadius: 2,
                      mb: 2,
                      bgcolor: license
                        ? isLicenseInactive(license)
                          ? "rgba(158, 158, 158, 0.04)"
                          : isLicenseExpired(license)
                            ? "rgba(244, 67, 54, 0.04)"
                            : isLicenseExpiring(license)
                              ? "rgba(255, 152, 0, 0.04)"
                              : "rgba(76, 175, 80, 0.04)"
                        : "background.default",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-1px)",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                      },
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      position: "relative",
                    }}
                  >
                    <ListItemIcon sx={{ mt: 0.5 }}>
                      <ComputerIcon color="primary" />
                    </ListItemIcon>

                    {/* Main Content Area */}
                    <Box sx={{ flex: 1, minWidth: 0, mr: 2 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="medium"
                        gutterBottom
                      >
                        {product.name}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          lineHeight: 1.4,
                          maxHeight: "2.8em",
                          wordBreak: "break-word",
                          mb: 1,
                        }}
                      >
                        {product.description || "No description available"}
                      </Typography>

                      {submission && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Hardware ID:{" "}
                            {submission.hardware_id.substring(0, 20)}...
                          </Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            Submitted:{" "}
                            {new Date(submission.submitted_at).toLocaleString()}
                          </Typography>
                          {submission.confirmed_at && (
                            <>
                              <br />
                              <Typography
                                variant="caption"
                                color="success.main"
                              >
                                Confirmed:{" "}
                                {new Date(
                                  submission.confirmed_at,
                                ).toLocaleString()}
                              </Typography>
                            </>
                          )}
                        </Box>
                      )}

                      {/* License Information */}
                      {license && (
                        <Box
                          sx={{
                            mt: 1,
                            pt: 1,
                            borderTop: 1,
                            borderColor: "divider",
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="primary.main"
                            sx={{ fontWeight: "bold" }}
                          >
                            License Key: {license.license_key.substring(0, 16)}
                            ...
                          </Typography>
                          <br />
                          {license.expiry_date && (
                            <>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Expires:{" "}
                                {new Date(
                                  license.expiry_date,
                                ).toLocaleDateString()}
                              </Typography>
                              <br />

                              {/* Status Chip */}
                              <Box sx={{ mt: 1 }}>
                                <Chip
                                  label={
                                    isLicenseInactive(license)
                                      ? "INACTIVE"
                                      : isLicenseExpired(license)
                                        ? "EXPIRED"
                                        : isLicenseExpiring(license)
                                          ? getDaysUntilExpiry(license) === 0
                                            ? "Expires Today"
                                            : getDaysUntilExpiry(license) === 1
                                              ? "Expires Tomorrow"
                                              : `${getDaysUntilExpiry(
                                                  license,
                                                )} Days Left`
                                          : "ACTIVE"
                                  }
                                  color={
                                    isLicenseInactive(license)
                                      ? "default"
                                      : isLicenseExpired(license)
                                        ? "error"
                                        : isLicenseExpiring(license)
                                          ? "warning"
                                          : "success"
                                  }
                                  size="small"
                                  sx={{
                                    fontWeight: "bold",
                                    fontSize: "0.7rem",
                                    height: 24,
                                    borderRadius: 1,
                                  }}
                                />

                                {/* Progress bar for expiring licenses */}
                                {isLicenseExpiring(license) &&
                                  !isLicenseExpired(license) && (
                                    <Box sx={{ mt: 0.5, width: "100px" }}>
                                      <Box
                                        sx={{
                                          width: "100%",
                                          height: 4,
                                          bgcolor: "grey.300",
                                          borderRadius: 2,
                                          overflow: "hidden",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            width: `${Math.max(
                                              0,
                                              Math.min(
                                                100,
                                                (getDaysUntilExpiry(license) /
                                                  10) *
                                                  100,
                                              ),
                                            )}%`,
                                            height: "100%",
                                            bgcolor:
                                              getDaysUntilExpiry(license) <= 3
                                                ? "error.main"
                                                : "warning.main",
                                            transition: "width 0.3s ease",
                                          }}
                                        />
                                      </Box>
                                    </Box>
                                  )}
                              </Box>
                            </>
                          )}
                        </Box>
                      )}
                    </Box>

                    {/* Right Side Actions */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: 1,
                        minWidth: 180,
                        flexShrink: 0,
                      }}
                    >
                      {/* Status Chip */}
                      <Chip
                        size="small"
                        label={status.label}
                        color={status.color}
                        icon={
                          status.status === "confirmed" ? (
                            <CheckIcon fontSize="small" />
                          ) : status.status === "locked" ? (
                            <LockIcon fontSize="small" />
                          ) : (
                            <PendingIcon fontSize="small" />
                          )
                        }
                      />

                      {/* Action Buttons */}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          flexWrap: "wrap",
                          justifyContent: "flex-end",
                        }}
                      >
                        {/* Copy License Button */}
                        {license && (
                          <Button
                            size="small"
                            variant="outlined"
                            color={
                              isLicenseInactive(license)
                                ? "inherit"
                                : isLicenseExpired(license)
                                  ? "error"
                                  : "secondary"
                            }
                            startIcon={<CopyIcon />}
                            onClick={() =>
                              copyLicenseKey(license, product.name)
                            }
                            sx={{
                              minWidth: 80,
                              fontSize: "0.7rem",
                              py: 0.5,
                              px: 1,
                              height: 32,
                            }}
                          >
                            COPY
                          </Button>
                        )}

                        {/* Submit/Edit Button */}
                        {canEdit && (
                          <Button
                            size="small"
                            variant={submission ? "outlined" : "contained"}
                            color={submission ? "primary" : "success"}
                            startIcon={submission ? <EditIcon /> : <SendIcon />}
                            onClick={() => openSubmissionDialog(product)}
                            sx={{
                              minWidth: 90,
                              fontSize: "0.7rem",
                              py: 0.5,
                              px: 1.5,
                              height: 32,
                              fontWeight: "bold",
                              ...(submission
                                ? {}
                                : {
                                    bgcolor: "success.main",
                                    "&:hover": {
                                      bgcolor: "success.dark",
                                    },
                                  }),
                            }}
                          >
                            {submission ? "EDIT" : "SUBMIT"}
                          </Button>
                        )}

                        {/* Locked Button */}
                        {!canEdit && (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled
                            sx={{
                              minWidth: 90,
                              fontSize: "0.7rem",
                              py: 0.5,
                              px: 1.5,
                              height: 32,
                            }}
                          >
                            LOCKED
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </ListItem>
                  {/* Removed divider since we now have bordered cards with spacing */}
                </React.Fragment>
              );
            })}
          </List>
        </CardContent>
      </Card>

      {/* Hardware ID Submission Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ComputerIcon color="primary" />
            <Typography variant="h6">
              {hardwareSubmissions.find(
                (sub) => sub.product.id === selectedProduct?.id,
              )
                ? "Update Hardware ID"
                : "Submit Hardware ID"}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedProduct && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Product: {selectedProduct.name}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                paragraph
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  lineHeight: 1.5,
                  maxHeight: "6em", // 4 lines * 1.5 line-height
                  wordBreak: "break-word",
                }}
              >
                {selectedProduct.description}
              </Typography>

              <TextField
                fullWidth
                label="Hardware ID"
                value={hardwareId}
                onChange={(e) => setHardwareId(e.target.value)}
                placeholder="Enter your hardware ID (base64 encoded)"
                margin="normal"
                required
                helperText="This should be a base64-encoded string provided by your application. Once confirmed by admin, this cannot be changed."
                multiline
                rows={3}
              />

              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Important:</strong> Make sure the hardware ID is
                  correct. Once confirmed by an administrator, you will not be
                  able to change it.
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitHardwareId}
            variant="contained"
            disabled={submitting || !hardwareId.trim()}
            startIcon={
              submitting ? <CircularProgress size={16} /> : <SendIcon />
            }
          >
            {submitting ? "Submitting..." : "Submit Hardware ID"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HardwareIdSubmission;
